import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useDebounce } from "primereact/hooks";
import { Invoice } from "../../api/models/invoices.models";
import ContainerHeader from "../../components/containerHeader";
import useAdminCompanies from "../../hooks/useAdminCompanies";
import useAdminInvoices from "../../hooks/useAdminInvoices";
import InvoiceTable from "../../views/invoiceTable";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../../components/loadingContainer";
import AdminInvoiceFilters, {
  InvoiceFilters,
} from "../../components/adminInvoiceFilters";
import { useAdminDataContext } from "../../contexts/adminDataContext";

const AllInvoicesContainer = styled.div`
  position: relative;
`;

const PaddedContainer = styled.div`
  padding: 6rem 2rem;
  text-align: left;
`;

const AdminInvoicingView: React.FC = () => {
  const { t } = useTranslation();

  const [filters, debouncedFilters, setFilters] = useDebounce<InvoiceFilters>(
    {
      month: undefined,
      quarter: undefined,
      year: undefined,
      company: undefined,
      invoiceNumber: undefined,
      status: undefined,
      skip: 0,
      take: 50,
    },
    500,
  );

  const [tableData, setTableData] = useState<
    (Invoice & { companyName?: string })[]
  >([]);

  const { companies, isLoadingCompanies } = useAdminDataContext();

  const { invoiceData, isLoadingInvoices, downloadInvoiceImage } =
    useAdminInvoices({
      companyId: debouncedFilters.company?.id,
      year: debouncedFilters.year,
      month: debouncedFilters.month,
      quarter: debouncedFilters.quarter,
      skip: debouncedFilters.skip,
      take: debouncedFilters.take,
      invoiceNumber: debouncedFilters.invoiceNumber,
    });

  useEffect(() => {
    if (invoiceData) {
      setTableData(
        invoiceData.data.map((invoice) => {
          const company = companies?.find((c) => c.id === invoice.companyId);
          return {
            ...invoice,
            companyName: company ? company.companyName : "",
          };
        }),
      );
    }
  }, [invoiceData, companies]);

  const handleFilterChange = (key: keyof InvoiceFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const setPage = (newPage: number) => {
    setFilters((prev) => ({
      ...prev,
      skip: (newPage - 1) * (prev.take || 50),
    }));
  };

  const setRows = (newRows: number) => {
    setFilters((prev) => ({ ...prev, take: newRows, skip: 0 }));
  };

  return (
    <AllInvoicesContainer>
      <ContainerHeader
        description={t("subheader-admin-invoicing")}
        title={t("header-admin-invoicing")}
      />
      <PaddedContainer>
        <LoadingContainer
          status={
            isLoadingCompanies
              ? HttpRequestStatus.LOADING
              : HttpRequestStatus.SUCCESS
          }
        >
          <AdminInvoiceFilters
            filters={filters}
            companies={companies}
            onFilterChange={handleFilterChange}
          />

          <LoadingContainer
            status={
              isLoadingInvoices
                ? HttpRequestStatus.LOADING
                : HttpRequestStatus.SUCCESS
            }
          >
            <InvoiceTable
              invoices={tableData || []}
              downloadInvoiceImageFn={downloadInvoiceImage}
              hideSorting={true}
              showCompanyName
              showPagination={true}
              rows={filters.take}
              page={Math.floor((filters.skip || 0) / (filters.take || 50)) + 1}
              setPage={setPage}
              setRows={setRows}
              totalReports={invoiceData?._count}
            />
          </LoadingContainer>
        </LoadingContainer>
      </PaddedContainer>
    </AllInvoicesContainer>
  );
};

export default AdminInvoicingView;
